import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import flvjs from "flv.js";

import {
  Alert, Box, Drawer, GlobalStyles, IconButton, Grid, Snackbar, Stack, ThemeProvider, Tooltip,
  createTheme, useMediaQuery
} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import MenuIcon from "@mui/icons-material/Menu";

import Navbar from "../appbar";
import { Wordmark } from "../Logo";

import { COLORS, INGEST_HOST, SRT_POLL_INTERVAL, VIDEO_POLL_INTERVAL } from "./constants";
import { getInitialUserId, getStorageValue } from "./utils";
import { useChunkedUpload } from "./useChunkedUpload";
import { buildPlatformList } from "./utils";

import { DeleteConfirmDialog } from "./DeleteConfirmDialog";
import { ConfirmDialog } from "./ConfirmDialog";
import { Sidebar, SECTION_IDS, SIDEBAR_WIDTH } from "./Sidebar";
import { SignalPath } from "./SignalPath";
import { PageHeader } from "./PageHeader";
import { SourcePanel, SOURCES } from "./SourcePanel";
import { RtmpIngestPanel } from "./RtmpIngestPanel";
import { BrowserEncodePanel } from "./BrowserEncodePanel";
import { FieldReportersPanel } from "./FieldReportersPanel";
import { Mp4SchedulePanel } from "./Mp4SchedulePanel";
import { Mp4Dropzone } from "./Mp4Dropzone";
import { OutputDestinationsPanel } from "./OutputDestinationsPanel";
import { DestinationsPanel } from "./DestinationsPanel";
import { ProblemAlerts } from "./ProblemAlerts";
import { SecurityBanners } from "./SecurityBanners";
import { isReauthCancelled } from "../../authClient";
import { Onboarding } from "./Onboarding";
import { UpNextPanel } from "./UpNextPanel";
import { FennOverlay } from "../FennLoader";
import { unsavedPlatforms } from "./destinationChanges";
import { ManageDestinationsDialog } from "./ManageDestinationsDialog";
import { LivePreviewPanel } from "./LivePreviewPanel";
import { SignalDiagnosticsPanel } from "./SignalDiagnosticsPanel";
import { MobileStatBar } from "./MobileStatBar";
import { MobileBottomNav, NAV_HEIGHT } from "./MobileBottomNav";


// MUI components take their font from the theme, not from the page wrapper, so
// without this the dashboard rendered in Roboto whatever the wrapper said.
// Dark mode + teal primary also fixes components left on MUI defaults (the
// default-blue Switch in the connect cards, light tooltips and menus).
const dashboardTheme = createTheme({
  palette: {
    mode: "dark",
    primary:    { main: COLORS.accent, contrastText: COLORS.onAccent },
    success:    { main: COLORS.green },
    warning:    { main: COLORS.amber },
    error:      { main: COLORS.red },
    background: { default: COLORS.bg, paper: COLORS.panel },
    text:       { primary: COLORS.text, secondary: COLORS.muted },
    divider:    COLORS.border
  },
  typography: {
    fontFamily: COLORS.fontSans,
    button: { textTransform: "none", fontWeight: 600 }
  },
  shape: { borderRadius: 11 },
  components: {
    MuiTooltip: {
      styleOverrides: {
        tooltip: { backgroundColor: COLORS.raised, color: COLORS.text, fontSize: 12, border: `1px solid ${COLORS.borderStrong}` }
      }
    }
  }
});

export default function Home() {
  const { id }   = useParams();
  const isMobile = useMediaQuery("(max-width:1100px)");

  // Opts the Studio out of App.css's old phone/tablet overrides (every Stack
  // forced into a column, every button full width). See the note there.
  useLayoutEffect(() => {
    document.body.classList.add("fn-studio");
    return () => document.body.classList.remove("fn-studio");
  }, []);

  // The DOM node the browser-camera preview is portalled into. Held in STATE
  // rather than a ref because BrowserEncodePanel has to re-render once the node
  // exists — a ref mutation would not trigger that, and the portal would never
  // be created.
  const [cameraPreviewHost, setCameraPreviewHost] = useState(null);

  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileOpen,  setMobileOpen]  = useState(false);
  const [tabIndex,    setTabIndex]    = useState(0);
  // Which ingest method the operator is working with. UI-only — the backend
  // doesn't care how bytes arrive, every path ends at the same NMS ingest.
  // Persisted because an operator who works over SRT should not be dropped
  // back onto the RTMP tab every time the dashboard reloads.
  const [sourceKind,  setSourceKind]  = useState(() => {
    const saved = getStorageValue("dextream.sourceKind");
    return SOURCES.some((s) => s.id === saved) ? saved : "rtmp";
  });

  useEffect(() => {
    try { window.localStorage.setItem("dextream.sourceKind", sourceKind); }
    catch (e) { /* private mode / quota — the default is harmless */ }
  }, [sourceKind]);

  const [youtubeKeys,      setYoutubeKeys]      = useState([]);
  const [facebookKeys,     setFacebookKeys]     = useState([]);
  const [tiktokKey,        setTiktokKey]        = useState("");
  const [rtmpDestinations, setRtmpDestinations] = useState([]);
  // Friendly names for fixed-key destinations, keyed by platform id
  // ("YouTube1", "TikTok", ...). Custom RTMP carries its own `name` instead.
  const [destLabels,       setDestLabels]       = useState({});
  const [streamStatus,     setStreamStatus]     = useState({});
  // Whether ANYTHING is publishing to the ingest, as opposed to whether a
  // particular destination is up. Drives which per-destination controls are
  // actually usable — see DestinationsPanel.
  const [platformStats,    setPlatformStats]    = useState({});
  const [ingestLive,       setIngestLive]       = useState(false);
  const [fileStreamLive,   setFileStreamLive]   = useState(false);
  const [retryLogs,        setRetryLogs]        = useState([]);

  // Connected platform accounts (OAuth) — sits alongside manual keys
  const [platformConnections, setPlatformConnections] = useState({ youtube: null, facebook: null });
  // Whether the first load of each has come back. First-time setup must not
  // decide "no destinations" from the empty initial state before then.
  const [keysLoaded,        setKeysLoaded]        = useState(false);
  // Whether the first load of each has come back at all, success or not:
  // what the opening logo animation waits for, so a failed request can't
  // hold it on screen.
  const [keysSettled,       setKeysSettled]       = useState(false);
  const [connSettled,       setConnSettled]       = useState(false);
  // What the server last returned for keys/labels, so the editor can tell
  // saved from unsaved and Discard can put it back.
  const [savedKeys, setSavedKeys] = useState(null);
  const [discardOpen, setDiscardOpen] = useState(false);
  const [connectionsLoaded, setConnectionsLoaded] = useState(false);
  const [connectBusy,         setConnectBusy]         = useState("");

  // Logged-in user's profile (full name / email) for the greeting + account bar
  const [userProfile, setUserProfile] = useState(null);

  // SRT: any field-reporter channel live, any camera connected — for the signal
  // path. The channels themselves live in FieldReportersPanel.
  const [srtStatus,          setSrtStatus]          = useState("offline");
  const [srtCameraConnected, setSrtCameraConnected] = useState(false);

  // RTMP / preview
  const [rtmpUrl,       setRtmpUrl]       = useState("");
  const [rtmpToken,     setRtmpToken]     = useState("");
  // The preview path only needs the account id, not the key, so it is known
  // from the start (storeSession keeps it) without revealing anything.
  const [displayUserId, setDisplayUserId] = useState(() => getStorageValue("userId") || "");
  const [rtmpKeyExpiresAt, setRtmpKeyExpiresAt] = useState(null);
  // "••••••••ab12" while a key is valid but not revealed on this page yet.
  const [rtmpKeyMasked, setRtmpKeyMasked] = useState("");
  const [previewUrl,    setPreviewUrl]    = useState("");

  const [message,         setMessage]         = useState("");
  const [messageSeverity, setMessageSeverity] = useState("info");
  const [copiedField,     setCopiedField]     = useState("");

  // null means "nothing measured", which is NOT the same as measured zeros —
  // the backend deliberately sends null rather than a zeroed object, and
  // SignalDiagnosticsPanel renders the two differently.
  const [metrics, setMetrics] = useState(null);
  // Why the last broadcast was refused or stopped (see ProblemAlerts.jsx).
  const [problems, setProblems] = useState([]);
  // Scheduled / running / finished MP4 jobs, for the "Up next" card.
  const [fileJobs, setFileJobs] = useState([]);

  // Epoch ms the current stream began, straight from the server — NOT captured
  // client-side when we first notice isLive. Deriving it here would restart
  // the clock on every page refresh and would be wrong for a stream that was
  // already running before this tab opened.
  const [startedAt, setStartedAt] = useState(null);

  // Rolling window of recent bitrate samples for the sparkline. Purely
  // client-side: the backend reports an instantaneous figure, and keeping a
  // short history is the client's job, not another thing for the server to
  // retain per user.
  const [bitrateHistory, setBitrateHistory] = useState([]);

  // Ticks once a second purely so the uptime label re-renders. Kept separate
  // from the 3s status poll: the clock should advance smoothly rather than
  // jumping in 3-second steps.
  const [nowTick, setNowTick] = useState(() => Date.now());
  useEffect(() => {
    if (!startedAt) return undefined;
    const t = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(t);
  }, [startedAt]);

  const uptimeLabel = useMemo(() => {
    if (!startedAt) return null;
    const secs = Math.max(0, Math.floor((nowTick - startedAt) / 1000));
    const h = String(Math.floor(secs / 3600)).padStart(2, "0");
    const m = String(Math.floor((secs % 3600) / 60)).padStart(2, "0");
    const s = String(secs % 60).padStart(2, "0");
    return `${h}:${m}:${s}`;
  }, [startedAt, nowTick]);

  // Video library
  const [uploadedVideos,  setUploadedVideos]  = useState([]);
  const [selectedVideoId, setSelectedVideoId] = useState("");
  const [storageSummary,  setStorageSummary]  = useState({
    usedBytes: 0, limitBytes: 10 * 1024 * 1024 * 1024, remainingBytes: 10 * 1024 * 1024 * 1024
  });

  // Scheduler
  const [scheduledAt,   setScheduledAt]   = useState("");
  const [schedulerBusy, setSchedulerBusy] = useState(false);

  // Delete dialog
  const [deleteTarget,     setDeleteTarget]     = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Stream-key / OAuth editor. Occasional setup work, so it lives behind a
  // dialog now rather than permanently occupying the left column.
  const [destDialogOpen,   setDestDialogOpen]   = useState(false);

  // isLoggedIn is derived from localStorage at render time
  const [isLoggedIn, setIsLoggedIn] = useState(!!localStorage.getItem("token"));
  const [userId,     setUserId]     = useState(() => getInitialUserId(id));

  const videoRef     = useRef(null);
  const flvPlayerRef = useRef(null);
  const fileInputRef = useRef(null);

  // Re-derive axiosConfig when isLoggedIn changes so a fresh login
  // picks up the new token without requiring a page reload.
  const axiosConfig = useMemo(() => {
    const token = localStorage.getItem("token");
    return {
      headers:         token ? { Authorization: `Bearer ${token}` } : {},
      withCredentials: true
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  const { state: uploadState, upload: doChunkedUpload, abort: abortUpload } =
    useChunkedUpload(axiosConfig);

  const platforms = useMemo(
    () => buildPlatformList({ youtubeKeys, facebookKeys, tiktokKey, rtmpDestinations, platformConnections, labels: destLabels }),
    [youtubeKeys, facebookKeys, tiktokKey, rtmpDestinations, platformConnections, destLabels]
  );

  // "Retrying" is INFERRED, not reported. The backend has no such state: a
  // platform whose FFmpeg copy died is simply absent from state.copies (so
  // /api/stream-status calls it offline) while fffmpegService sits in its
  // backoff sleep before respawning. What we do get is retryLogs — a rolling
  // 50-entry diagnostic history of exits per platform. A recent entry for a
  // platform that isn't currently live, while the ingest IS live, means a
  // reconnect is in flight rather than a destination the user switched off.
  // Distinguishing those two matters: one needs no action, the other does.
  const retryingIds = useMemo(() => {
    const RECENT_MS = 45_000;
    const cutoff = Date.now() - RECENT_MS;
    const ids = new Set();
    if (!ingestLive) return ids;
    retryLogs.forEach((entry) => {
      if (!entry?.platform) return;
      const at = Date.parse(entry.timestamp);
      if (Number.isNaN(at) || at < cutoff) return;
      if (streamStatus[entry.platform] !== "live") ids.add(entry.platform);
    });
    return ids;
  }, [retryLogs, streamStatus, ingestLive]);

  const fullName = useMemo(
    // The account's name (PLAN.md); older accounts' first + last name until they set one.
    () => (userProfile
      ? userProfile.displayName || `${userProfile.firstName || ""} ${userProfile.lastName || ""}`.trim() || "Fennira account"
      : ""),
    [userProfile]
  );

  const liveCount = platforms.filter(({ id: pid }) => streamStatus[pid] === "live").length;

  const showMessage = useCallback((text, severity = "info") => {
    setMessage(text); setMessageSeverity(severity);
  }, []);

  useEffect(() => { setUserId(getInitialUserId(id)); }, [id]);

  // ── Data fetchers ──────────────────────────────────────────────────────────
  const fetchKeys = useCallback(async () => {
    try {
      const res  = await axios.get("/api/stream-keys", axiosConfig);
      const keys = res.data?.keys || {};
      const snapshot = {
        youtubeKeys:      (keys.youtubeKeys  || []).filter(Boolean),
        facebookKeys:     (keys.facebookKeys || []).filter(Boolean),
        tiktokKey:        keys.tiktokKey || "",
        rtmpDestinations: (keys.rtmpDestinations || []).filter((d) => d?.url && d?.key),
        labels:           keys.labels && typeof keys.labels === "object" ? keys.labels : {}
      };
      setSavedKeys(snapshot);
      setYoutubeKeys(snapshot.youtubeKeys);
      setFacebookKeys(snapshot.facebookKeys);
      setTiktokKey(snapshot.tiktokKey);
      setRtmpDestinations(snapshot.rtmpDestinations);
      setDestLabels(snapshot.labels);
      setKeysLoaded(true);
    } catch (err) {
      // During a recovery's 24 hours the banner explains why; no toast.
      if (err?.response?.data?.code !== "recovery_restricted") showMessage("Unable to load saved stream keys", "warning");
    }
    finally { setKeysSettled(true); }
  }, [axiosConfig, showMessage]);

  const fetchStreamStatus = useCallback(async () => {
    try {
      const res = await axios.get("/api/stream-status", axiosConfig);
      setStreamStatus(res.data?.platformStatus || {});
      // Per-destination fps / bitrate / speed / startedAt. The backend has
      // returned this since the diagnostics work; nothing consumed it until
      // the per-destination timings needed it.
      setPlatformStats(res.data?.platformStats || {});
      // isLive and retryLogs were both being thrown away here. isLive is the
      // difference between "this destination is stopped" and "nothing is
      // publishing at all", which is what decides whether the per-destination
      // controls can do anything.
      // ingestLive answers "is an encoder connected", which is what the
      // per-destination Start buttons need. isLive only says "some copy process
      // is running", so stopping the last destination reported the ingest as
      // idle while OBS was still pushing — and disabled every Start button.
      // Falls back to isLive for a backend that predates the field.
      setIngestLive(Boolean(res.data?.ingestLive ?? res.data?.isLive));
      // Frames can be flowing from a scheduled MP4 job even though the RTMP
      // ingest is idle. Tracked separately because /api/stream-control only
      // knows about ingest streams — see the backend comment on isLive.
      setFileStreamLive(Boolean(res.data?.fileStreamLive));
      setRetryLogs(Array.isArray(res.data?.retryLogs) ? res.data.retryLogs : []);
      setProblems(Array.isArray(res.data?.problems) ? res.data.problems : []);
      // Real numbers now: fffmpegService parses FFmpeg's stderr progress lines
      // per destination and the controller aggregates them, dropping any
      // reading older than 8s so a dead process can't leave a frozen bitrate
      // on screen.
      const nextMetrics = res.data?.metrics || null;
      setMetrics(nextMetrics);
      setStartedAt(res.data?.startedAt ?? null);

      // Only record real readings. Pushing a 0 for every idle poll would draw
      // a long flat run that looks like measured silence rather than absence,
      // and would flatten the scale of the real data either side of it.
      setBitrateHistory((prev) => {
        if (nextMetrics?.bitrate == null) return prev.length ? [] : prev;
        const next = [...prev, nextMetrics.bitrate];
        return next.length > 40 ? next.slice(next.length - 40) : next;
      });
    } catch (err) {
      // Polled every 3s — a toast here would spam on any blip.
      console.error("[stream-status] poll failed:", err.message);
    }
  }, [axiosConfig]);

  const fetchFileJobs = useCallback(async () => {
    try {
      const res = await axios.get("/api/stream-status/file-jobs", axiosConfig);
      setFileJobs(Array.isArray(res.data?.jobs) ? res.data.jobs : []);
    } catch { /* polled; the card simply keeps its last list */ }
  }, [axiosConfig]);

  useEffect(() => {
    if (!isLoggedIn) return undefined;
    fetchFileJobs();
    const iv = setInterval(fetchFileJobs, 30_000);
    return () => clearInterval(iv);
  }, [fetchFileJobs, isLoggedIn]);

  const cancelFileJob = useCallback(async (job) => {
    try {
      await axios.post(`/api/stream-status/file-jobs/${encodeURIComponent(job.id)}/stop`, {}, axiosConfig);
      showMessage("Scheduled stream cancelled. The video is still in your library", "success");
    } catch (err) {
      showMessage(err.response?.data?.message || "Could not cancel the scheduled stream", "error");
    }
    fetchFileJobs();
  }, [axiosConfig, fetchFileJobs, showMessage]);

  const fetchSRTStatus = useCallback(async () => {
    try {
      const res = await axios.get("/srt/channels", axiosConfig);
      const channels = res.data?.channels || [];
      setSrtStatus(channels.some((c) => c.live) ? "live" : "offline");
      setSrtCameraConnected(channels.some((c) => c.cameraConnected));
    } catch {}
  }, [axiosConfig]);

  const fetchVideos = useCallback(async () => {
    try {
      const res    = await axios.get("/api/file-stream/videos", axiosConfig);
      const videos = res.data?.videos || [];
      setUploadedVideos(videos);
      if (res.data?.storage) setStorageSummary(res.data.storage);
      setSelectedVideoId((cur) =>
        cur && videos.some((v) => v.id === cur) ? cur : videos[0]?.id || ""
      );
    } catch {}
  }, [axiosConfig]);

  const fetchConnections = useCallback(async () => {
    try {
      const res = await axios.get("/api/connect/status", axiosConfig);
      setPlatformConnections({
        youtube:  res.data?.youtube  || null,
        facebook: res.data?.facebook || null
      });
      setConnectionsLoaded(true);
    } catch {}
    finally { setConnSettled(true); }
  }, [axiosConfig]);

  const fetchProfile = useCallback(async () => {
    try {
      const res = await axios.get("/api/profile", axiosConfig);
      setUserProfile(res.data?.user || null);
    } catch {}
  }, [axiosConfig]);

  // ── Polling ────────────────────────────────────────────────────────────────
  useEffect(() => { fetchKeys(); }, [fetchKeys]);

  useEffect(() => { if (isLoggedIn) fetchProfile(); }, [fetchProfile, isLoggedIn]);

  useEffect(() => {
    fetchStreamStatus();
    const iv = setInterval(fetchStreamStatus, 3000);
    return () => clearInterval(iv);
  }, [fetchStreamStatus]);

  useEffect(() => {
    fetchSRTStatus();
    const iv = setInterval(fetchSRTStatus, SRT_POLL_INTERVAL);
    return () => clearInterval(iv);
  }, [fetchSRTStatus]);

  useEffect(() => {
    // Gated on isLoggedIn (not userId) — this endpoint only needs a valid
    // auth token, the server derives the account from the JWT.
    if (!isLoggedIn) return;
    fetchVideos();
    const iv = setInterval(fetchVideos, VIDEO_POLL_INTERVAL);
    return () => clearInterval(iv);
  }, [fetchVideos, isLoggedIn]);

  useEffect(() => { fetchConnections(); }, [fetchConnections]);

  // Pick up the redirect back from a YouTube/Facebook "Connect Account" flow
  useEffect(() => {
    const params    = new URLSearchParams(window.location.search);
    const connected = params.get("connected");
    const name      = params.get("name");
    const failed    = params.get("connectError");
    const reason    = params.get("reason");

    if (connected) {
      const label = connected === "youtube" ? "YouTube" : "Facebook";
      showMessage(`${label} connected${name ? ` — ${name}` : ""}`, "success");
      fetchConnections();
      params.delete("connected"); params.delete("name");
      window.history.replaceState({}, "", `${window.location.pathname}${params.toString() ? `?${params}` : ""}`);
    }
    if (failed) {
      const label = failed === "youtube" ? "YouTube" : "Facebook";
      showMessage(`Could not connect ${label}${reason ? ` — ${reason}` : " — please try again"}`, "error");
      params.delete("connectError"); params.delete("reason");
      window.history.replaceState({}, "", `${window.location.pathname}${params.toString() ? `?${params}` : ""}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Which picture belongs to the selected source ─────────────────────────
  //
  // previewUrl points at /live/<userId>.flv — the RTMP INGEST. It used to be
  // handed to the preview for the SRT source as well, because showsRoundTrip
  // was true for both. So selecting SRT replayed whatever OBS was pushing,
  // under a caption reading "Returned from the SRT relay" — the RTMP stream
  // wearing an SRT label. Streaming over RTMP while sitting on the SRT tab
  // showed a perfectly good picture that had nothing to do with SRT.
  //
  // The SRT relay is camera -> VPS -> viewer over SRT end to end. It never
  // publishes to node-media-server, so there is no .flv for it to serve and
  // NO url can currently be produced here. Empty is the honest answer; the
  // panel says so rather than borrowing another source's video.
  const srtPreviewUrl = "";

  const activePreviewUrl =
    sourceKind === "rtmp" ? previewUrl :
    sourceKind === "srt"  ? srtPreviewUrl : "";

  // ── FLV preview ────────────────────────────────────────────────────────────
  //
  // node-media-server does NOT 404 an HTTP-FLV request for a stream nobody is
  // publishing: node_flv_session.js's onPlay() adds the request to idlePlayers
  // and returns WITHOUT writing a response, holding the socket open until a
  // publisher shows up. The reverse proxy in front of it eventually gives up
  // and turns that silence into a 504. So from the browser, "the encoder
  // hasn't connected yet" is indistinguishable from a real failure — both
  // arrive as an HTTP error on the .flv URL. Hence the bounded retry below.
  useEffect(() => {
    // Only the RTMP and SRT sources have a server round-trip to pull. The
    // browser source already has the picture locally (BrowserEncodePanel puts
    // the MediaStream on its own <video> via srcObject), so fetching the FLV
    // here too would be duplicate traffic AND would reintroduce the 504/retry
    // loop for a stream we are holding in memory. The MP4 source has no live
    // signal at all.
    if (sourceKind !== "rtmp" && sourceKind !== "srt") return;
    if (!activePreviewUrl || !videoRef.current) return;
    if (!flvjs.isSupported()) { showMessage("FLV not supported in this browser", "warning"); return; }

    const MAX_RETRIES = 4;
    const RETRY_DELAY_MS = 4000;

    let disposed = false;
    let retries  = 0;
    let timer    = null;

    const teardown = () => {
      if (timer) { clearTimeout(timer); timer = null; }
      const player = flvPlayerRef.current;
      flvPlayerRef.current = null;
      if (!player) return;
      // Each of these can throw if the player already errored out, and a throw
      // here would strand the next player on a still-attached media element.
      try { player.pause(); }               catch (e) { /* already stopped */ }
      try { player.unload(); }              catch (e) { /* already unloaded */ }
      try { player.detachMediaElement(); }  catch (e) { /* already detached */ }
      try { player.destroy(); }             catch (e) { /* already destroyed */ }
    };

    const attach = () => {
      if (disposed || !videoRef.current) return;
      teardown();

      const player = flvjs.createPlayer({
        type: "flv", url: activePreviewUrl, isLive: true,
        enableStashBuffer: false, autoCleanupSourceBuffer: true
      });
      flvPlayerRef.current = player;

      // REQUIRED, not defensive: flv.js emits through Node's EventEmitter
      // (bundled), which THROWS "Unhandled error. (undefined)" when an
      // `error` event has no listener. Without this the 504 above surfaced as
      // an uncaught promise rejection from flv-player.js that the user could
      // neither see nor act on.
      player.on(flvjs.Events.ERROR, (errorType, errorDetail, errorInfo) => {
        const code = errorInfo?.code;
        console.error("[preview] flv.js error:", errorType, errorDetail, code, errorInfo?.msg);
        if (disposed) return;

        // Detail strings verified against the installed flv.js 1.6.2 bundle:
        // HttpStatusCodeInvalid | ConnectingTimeout | EarlyEof |
        // UnrecoverableEarlyEof | Exception, with `info` carrying {code, msg}.
        //
        // 504 = the proxy gave up on an idle NMS request; 404 = the path isn't
        // there yet; ConnectingTimeout is that same silence observed from the
        // browser instead of the proxy. Any OTHER status (403, 500, …) is a
        // genuine fault and must NOT be disguised as "waiting for the encoder".
        const waitingForIngest =
          code === 504 || code === 404 || errorDetail === "ConnectingTimeout";

        if (waitingForIngest && retries < MAX_RETRIES) {
          retries += 1;
          showMessage(
            `Waiting for the encoder to connect… (attempt ${retries}/${MAX_RETRIES})`,
            "info"
          );
          timer = setTimeout(attach, RETRY_DELAY_MS);
          return;
        }

        teardown();
        showMessage(
          waitingForIngest
            ? "No incoming stream yet. Start pushing from OBS or your camera, then press play."
            : `Preview failed: ${errorDetail || errorType}${code ? ` (HTTP ${code})` : ""}`,
          waitingForIngest ? "warning" : "error"
        );
      });

      player.attachMediaElement(videoRef.current);
      player.load();
      player.play().catch(() => showMessage("Preview ready — press play.", "info"));
    };

    attach();

    return () => { disposed = true; teardown(); };
    // sourceKind is a dependency so switching away from RTMP/SRT runs the
    // cleanup below and actually releases the player, rather than leaving a
    // socket open behind a hidden panel.
  }, [activePreviewUrl, showMessage, sourceKind]);

  // ── Upload ─────────────────────────────────────────────────────────────────
  const handleFileSelect = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".mp4")) {
      showMessage("Only MP4 files are allowed", "warning"); return;
    }
    const result = await doChunkedUpload(file);
    if (result) {
      showMessage(`${file.name} uploaded successfully`, "success");
      await fetchVideos();
      if (result.id) setSelectedVideoId(result.id);
    }
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // ── Schedule ───────────────────────────────────────────────────────────────
  const handleSchedule = async (e) => {
    e.preventDefault();
    if (!selectedVideoId) { showMessage("Select a video from the library", "warning"); return; }
    if (!scheduledAt)     { showMessage("Choose a schedule time", "warning"); return; }
    setSchedulerBusy(true);
    try {
      await axios.post(
        "/api/file-stream/schedule",
        { videoId: selectedVideoId, scheduledAt: new Date(scheduledAt).toISOString() },
        axiosConfig
      );
      setScheduledAt("");
      showMessage("MP4 stream scheduled", "success");
      fetchFileJobs();
    } catch (err) {
      showMessage(err.response?.data?.message || "Could not schedule stream", "error");
    } finally { setSchedulerBusy(false); }
  };

  // ── Delete job ─────────────────────────────────────────────────────────────
  // Removes a BROADCAST LOG entry only. The uploaded video is deleted by
  // deleteVideo() below, from the library — the two are deliberately separate,
  // so tidying the log can never destroy a file.
  const confirmDelete = async (job) => {
    try {
      await axios.delete(
        `/api/file-stream/jobs/${encodeURIComponent(job.id)}`,
        axiosConfig
      );
      showMessage("Removed from the broadcast log — your video is untouched", "success");
      // No fetchVideos(): nothing about the library changed.
    } catch { showMessage("Could not remove this log entry", "error"); }
  };

  // ── Delete video ───────────────────────────────────────────────────────────
  // Asks first in the app's own dialog (rendered below) instead of
  // window.confirm, which looked nothing like the app and can be suppressed.
  const [videoToDelete,   setVideoToDelete]   = useState(null);
  const [videoDeleteOpen, setVideoDeleteOpen] = useState(false);
  const deleteVideo = (video) => { setVideoToDelete(video); setVideoDeleteOpen(true); };

  const performDeleteVideo = async (video) => {
    try {
      const res    = await axios.delete(
        `/api/file-stream/videos/${encodeURIComponent(video.id)}`,
        axiosConfig
      );
      const videos = res.data?.videos || [];
      setUploadedVideos(videos);
      if (res.data?.storage) setStorageSummary(res.data.storage);
      setSelectedVideoId((cur) => (cur === video.id ? videos[0]?.id || "" : cur));
      showMessage("Video deleted", "success");
      // Deleting a video cancels its scheduled streams.
      fetchFileJobs();
    } catch (err) {
      showMessage(err.response?.data?.message || "Could not delete video", "error");
    }
  };

  // ── RTMP token ────────────────────────────────────────────────────────────
  // `silent`: the automatic load on opening the page, which should not pop
  // a "generated" toast nobody asked for.
  const generateStreamToken = async ({ silent = false } = {}) => {
    try {
      // The key itself: needs a passkey check in the last 5 minutes, which
      // authClient asks for if the server says so.
      const res = await axios.get("/token/reveal", axiosConfig);
      const { token, userId: uid } = res.data || {};
      if (!token || !uid) { showMessage("Token response missing fields", "warning"); return; }
      setRtmpUrl(`rtmp://${INGEST_HOST}/live`);
      setRtmpToken(token);
      setDisplayUserId(uid);
      setRtmpKeyExpiresAt(Number(res.data?.expiresAt) || null);
      setRtmpKeyMasked("");
      // The silent load on page open must not start the preview: with no
      // encoder yet it spends its few retries (and toasts) at once and gives
      // up. The effect below starts it when the encoder actually connects.
      if (!silent) setPreviewUrl(`https://${INGEST_HOST}/live/${uid}.flv`);
      if (!silent) showMessage("Stream key ready", "success");
    } catch (err) { if (!silent) reportError(err, "Could not get a stream key"); }
  };

  // One place to turn a failed sensitive action into a message: nothing when
  // the person cancelled "Confirm it's you", a plain explanation during a
  // recovery's 24 hours, the server's own words otherwise.
  const reportError = useCallback((err, fallback) => {
    if (isReauthCancelled(err)) return;
    if (err?.response?.data?.code === "recovery_restricted") {
      showMessage("Locked for 24 hours after a recovery-code sign-in.", "warning");
      return;
    }
    showMessage(err?.response?.data?.message || fallback, "error");
  }, [showMessage]);

  // On opening, only whether a key is valid and until when (/token/status
  // returns no secret), so Encoder settings can say "valid until 18:40"
  // without asking for a passkey. The key itself is fetched on "Show key".
  const keyAutoLoaded = useRef(false);
  useEffect(() => {
    if (!isLoggedIn || keyAutoLoaded.current) return;
    keyAutoLoaded.current = true;
    axios.get("/token/status", { ...axiosConfig, skipReauth: true })
      .then(({ data }) => {
        if (data?.hasKey) {
          setRtmpKeyExpiresAt(Number(data.expiresAt) || null);
          setRtmpKeyMasked(data.masked || "");
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  // Picture as soon as the encoder is connected (on the RTMP source), rather
  // than only when a preview attempt happened to be running at that moment.
  // A preview the user stopped on purpose stays stopped until this stream ends.
  const previewStoppedByUser = useRef(false);
  useEffect(() => {
    if (!ingestLive) { previewStoppedByUser.current = false; return; }
    if (displayUserId && !previewUrl && sourceKind === "rtmp" && !previewStoppedByUser.current) {
      setPreviewUrl(`https://${INGEST_HOST}/live/${displayUserId}.flv`);
    }
  }, [ingestLive, displayUserId, previewUrl, sourceKind]);

  const startPreview = () => {
    if (!displayUserId) { showMessage("Generate a stream token first", "warning"); return; }
    previewStoppedByUser.current = false;
    setPreviewUrl(`https://${INGEST_HOST}/live/${displayUserId}.flv`);
  };

  // Clearing previewUrl is enough: the FLV effect's cleanup owns teardown (and
  // does it inside try/catch, and cancels any pending retry). Tearing the
  // player down a second time here risked throwing on an already-errored
  // player BEFORE setPreviewUrl(""), which left the UI stuck showing a dead
  // preview that the stop button could no longer clear.
  const stopPreview = () => { previewStoppedByUser.current = true; setPreviewUrl(""); };


  const copyToClipboard = async (text, fieldId) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldId);
      setTimeout(() => setCopiedField(""), 1500);
    } catch { showMessage("Clipboard unavailable", "warning"); }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      await axios.post("/api/stream-keys", {
        userId,
        youtubeKeys:      youtubeKeys.filter(Boolean),
        facebookKeys:     facebookKeys.filter(Boolean),
        tiktokKey,
        rtmpDestinations: rtmpDestinations.filter((d) => d.url && d.key),
        rtmpUrls:         rtmpDestinations.filter((d) => d.url && d.key),
        labels:           destLabels
      }, axiosConfig);
      // Re-read rather than trusting local state: the server trims, length-caps
      // and drops blank labels, so this is what actually got stored.
      fetchKeys();
      showMessage("Channel configuration saved", "success");
      fetchStreamStatus();
      // Close only on success — a failed save must keep the user's typed keys
      // on screen to correct, not discard them behind a closed dialog.
      setDestDialogOpen(false);
    } catch (err) {
      reportError(err, "Could not save channel configuration");
    }
  };

  // ── Destinations editor: unsaved changes ──────────────────────────────────
  const unsavedDest = useMemo(() => unsavedPlatforms(
    { youtubeKeys, facebookKeys, tiktokKey, rtmpDestinations, labels: destLabels },
    savedKeys
  ), [youtubeKeys, facebookKeys, tiktokKey, rtmpDestinations, destLabels, savedKeys]);

  const discardDestChanges = useCallback(() => {
    if (!savedKeys) return;
    setYoutubeKeys(savedKeys.youtubeKeys);
    setFacebookKeys(savedKeys.facebookKeys);
    setTiktokKey(savedKeys.tiktokKey);
    setRtmpDestinations(savedKeys.rtmpDestinations);
    setDestLabels(savedKeys.labels);
  }, [savedKeys]);

  // Closing with unsaved edits asks first. Before, the typed values simply
  // stayed in memory and reappeared next time looking as if they were saved.
  const requestCloseDest = useCallback(() => {
    if (unsavedDest.length) setDiscardOpen(true);
    else setDestDialogOpen(false);
  }, [unsavedDest.length]);

  // ── Platform controls ──────────────────────────────────────────────────────
  // Both of these surface the server's own message ("Platform not live",
  // "User not live", "Streaming destination not found") instead of a flat
  // "Could not stop X" — those three mean very different things and only the
  // server knows which one happened.
  const stopPlatformApi = async (platform) => {
    try {
      await axios.post(`/api/stream-control/stop/${encodeURIComponent(platform)}`, {}, axiosConfig);
      showMessage(`${platform} stopped`, "success"); fetchStreamStatus();
    } catch (err) {
      showMessage(err.response?.data?.message || `Could not stop ${platform}`, "error");
      fetchStreamStatus();
    }
  };

  const restartPlatformApi = async (platform) => {
    try {
      await axios.post(`/api/stream-control/restart/${encodeURIComponent(platform)}`, {}, axiosConfig);
      showMessage(`${platform} restarted`, "success"); fetchStreamStatus();
    } catch (err) {
      showMessage(err.response?.data?.message || `Could not restart ${platform}`, "error");
      fetchStreamStatus();
    }
  };

  // ── Problem alerts ─────────────────────────────────────────────────────────
  // The server picks the fix; this only knows how to carry each kind out.
  const handleProblemFix = useCallback((problem) => {
    const fix = problem?.fix;
    if (!fix) return;
    if (fix.type === "manage") { setDestDialogOpen(true); return; }
    if (fix.type === "link" && /^https:\/\//.test(fix.href || "")) {
      window.open(fix.href, "_blank", "noopener,noreferrer");
      return;
    }
    if (fix.type === "source") {
      setSourceKind("rtmp");
      document.getElementById(SECTION_IDS.source)
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, []);

  // Removed at once; the next poll would bring it back if the server refused.
  const handleProblemDismiss = useCallback(async (problem) => {
    setProblems((prev) => prev.filter((p) => p.id !== problem.id));
    try {
      await axios.post(
        `/api/stream-status/problems/${encodeURIComponent(problem.id)}/dismiss`, {}, axiosConfig
      );
    } catch (err) {
      console.error("[problems] dismiss failed:", err.message);
    }
  }, [axiosConfig]);

  // ── Connected platform accounts (OAuth) ─────────────────────────────────────
  const connectPlatform = async (platform) => {
    setConnectBusy(platform);
    try {
      const res = await axios.get(`/api/connect/${platform}/url`, axiosConfig);
      if (res.data?.url) {
        window.location.href = res.data.url; // full-page redirect to consent screen
      } else {
        setConnectBusy("");
      }
    } catch (err) {
      reportError(err, `Could not start ${platform === "youtube" ? "YouTube" : "Facebook"} connect flow`);
      setConnectBusy("");
    }
  };

  const disconnectPlatform = async (platform) => {
    try {
      await axios.delete(`/api/connect/${platform}`, axiosConfig);
      showMessage(`${platform === "youtube" ? "YouTube" : "Facebook"} disconnected`, "success");
      fetchConnections();
    } catch (err) { reportError(err, `Could not disconnect ${platform === "youtube" ? "YouTube" : "Facebook"}`); }
  };

  const toggleConnection = async (platform, enabled) => {
    try {
      await axios.post(`/api/connect/${platform}/toggle`, { enabled }, axiosConfig);
      fetchConnections();
    } catch { showMessage("Could not update connection", "error"); }
  };

  const saveMetadata = async (platform, field, value) => {
    try {
      await axios.patch(`/api/connect/${platform}/metadata`, { [field]: value }, axiosConfig);
    } catch { showMessage("Could not save title/description", "error"); }
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  // ── First-time setup ──────────────────────────────────────────────────────
  // Shown once to an account with no destinations. The step is kept in
  // localStorage so the YouTube/Facebook sign-in redirect (a full page load)
  // lands the user back where they were. Accounts that already have
  // destinations are marked done without ever seeing it.
  const setupKey = `fennira.setup.${userId || "me"}`;
  const [setupOpen, setSetupOpen] = useState(false);
  const [setupStep, setSetupStep] = useState(1);
  const setupChecked = useRef(false);

  const saveSetup = useCallback((value) => {
    try { window.localStorage.setItem(setupKey, String(value)); }
    catch (e) { /* private mode: setup simply shows again next time */ }
  }, [setupKey]);

  useEffect(() => {
    if (setupChecked.current || !keysLoaded || !connectionsLoaded) return;
    setupChecked.current = true;
    const saved = getStorageValue(setupKey);
    if (saved === "done") return;
    if (["1", "2", "3"].includes(saved)) { setSetupStep(Number(saved)); setSetupOpen(true); return; }
    if (platforms.length === 0) { setSetupStep(1); setSetupOpen(true); }
    else saveSetup("done");
  }, [keysLoaded, connectionsLoaded, platforms.length, setupKey, saveSetup]);

  useEffect(() => { if (setupOpen) saveSetup(setupStep); }, [setupOpen, setupStep, saveSetup]);

  const closeSetup = useCallback(() => { saveSetup("done"); setSetupOpen(false); }, [saveSetup]);

  const openSetup = useCallback(() => {
    setSetupStep(platforms.length ? 2 : 1);
    setSetupOpen(true);
  }, [platforms.length]);

  const accountBar = (
    <Navbar
      compact
      isLoggedIn={isLoggedIn} setIsLoggedIn={setIsLoggedIn}
      userId={userId}          setUserId={setUserId}
      displayName={fullName}
    />
  );

  return (
    // textAlign: CRA's App.css sets `.App { text-align: center }` on every route,
    // which centred every heading, label and destination name on this page.
    <ThemeProvider theme={dashboardTheme}>
    <Box sx={{ minHeight: "100vh", bgcolor: COLORS.bg, color: COLORS.text, textAlign: "left", fontFamily: COLORS.fontSans }}>
      {/* Your logo until destinations and accounts have loaded, instead of
          an empty dashboard filling in piece by piece. */}
      <FennOverlay show={!(keysSettled && connSettled)} label="Opening your Studio" />

      <GlobalStyles styles={{
        body: { margin: 0, backgroundColor: COLORS.bg },
        // App.css still styles every input[type="text"] from the old theme
        // (own border, fill, padding and a 10px bottom margin). Inside an MUI
        // field that drew a box within the box and pushed the end adornment
        // (the copy button) outside the field; password fields were spared,
        // which is why only the Server field looked broken. Reset here, at
        // higher specificity, for MUI inputs only, including the ones MUI
        // portals into dialogs. Padding restates MUI's own values.
        ".MuiInputBase-input[type='text']": {
          margin: 0, border: 0, borderRadius: 0, backgroundColor: "transparent",
          color: "inherit", fontSize: "inherit", width: "100%", padding: "16.5px 14px"
        },
        ".MuiInputBase-inputSizeSmall[type='text']": { padding: "8.5px 14px" },
        ".MuiInputBase-inputAdornedStart[type='text']": { paddingLeft: 0 },
        ".MuiInputBase-inputAdornedEnd[type='text']": { paddingRight: 0 },
        "@keyframes pulse": { "0%,100%": { opacity: 0.38 }, "50%": { opacity: 1 } },
        "@keyframes spin":  { "0%": { transform: "rotate(0deg)" }, "100%": { transform: "rotate(360deg)" } },
        "::-webkit-scrollbar": { width: 8, height: 8 },
        "::-webkit-scrollbar-track": { background: "rgba(255,255,255,0.03)" },
        "::-webkit-scrollbar-thumb": { background: "rgba(255,255,255,0.14)", borderRadius: 8 }
      }} />

      <DeleteConfirmDialog
        job={deleteTarget} open={deleteDialogOpen}
        onClose={() => { setDeleteDialogOpen(false); setDeleteTarget(null); }}
        onConfirm={confirmDelete}
      />

      <ConfirmDialog
        open={videoDeleteOpen}
        onClose={() => setVideoDeleteOpen(false)}
        onConfirm={() => performDeleteVideo(videoToDelete)}
        icon={<DeleteOutlineIcon />}
        title={`Delete "${videoToDelete?.originalName || videoToDelete?.fileName || "this video"}"?`}
        body="It's removed from your library, and any scheduled streams that use it are cancelled. This can't be undone."
        cancelLabel="Keep it"
        confirmLabel="Delete video"
      />

      {isMobile && <MobileBottomNav />}

      {/* Feedback used to be a full-width Alert at the top of the page: off
          screen when the action was further down, and left standing long after
          it mattered ("Stream token generated" while already live). Successes
          and info now close themselves; warnings and errors stay until closed.
          key={message} restarts the auto-hide timer when the text changes. */}
      <Snackbar
        key={message}
        open={Boolean(message)}
        onClose={(_e, reason) => { if (reason !== "clickaway") setMessage(""); }}
        autoHideDuration={messageSeverity === "error" || messageSeverity === "warning" ? null : 4000}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        sx={isMobile ? { bottom: `${NAV_HEIGHT + 16}px !important` } : undefined}
      >
        <Alert
          severity={messageSeverity} onClose={() => setMessage("")} variant="outlined"
          sx={{
            width: "100%", maxWidth: 420, alignItems: "center",
            bgcolor: "#2A2760", color: COLORS.text,
            border: `1px solid ${COLORS.borderStrong}`, borderRadius: "12px",
            boxShadow: "0 18px 48px rgba(0,0,0,0.45)"
          }}
        >
          {message}
        </Alert>
      </Snackbar>

      {/* The desktop sidebar collapses to width 0, which takes its own
          collapse/expand button with it. Without this there was no way back. */}
      {!isMobile && !sidebarOpen && (
        <Tooltip title="Show sidebar" placement="right">
          <IconButton
            onClick={() => setSidebarOpen(true)}
            aria-label="Show sidebar"
            sx={{
              position: "fixed", top: 16, left: 12, zIndex: 1300,
              color: COLORS.text, bgcolor: COLORS.band,
              border: `1px solid ${COLORS.border}`, borderRadius: "10px",
              "&:hover": { bgcolor: "#2A2760" }
            }}
          >
            <ChevronRightIcon />
          </IconButton>
        </Tooltip>
      )}

      <Onboarding
        open={setupOpen}
        firstName={userProfile?.displayName || userProfile?.firstName || ""}
        step={setupStep} setStep={setSetupStep}
        onSkip={closeSetup} onFinish={closeSetup}
        platforms={platforms} platformConnections={platformConnections}
        connectBusy={connectBusy} onConnect={connectPlatform}
        onAddWithKey={(tab) => { setTabIndex(tab); setDestDialogOpen(true); }}
        rtmpUrl={rtmpUrl || `rtmp://${INGEST_HOST}/live`}
        streamKey={rtmpToken && displayUserId ? `${displayUserId}?sign=${rtmpToken}` : ""}
        keyExpiresAt={rtmpKeyExpiresAt} onGetKey={generateStreamToken}
        copiedField={copiedField} copyToClipboard={copyToClipboard}
        ingestLive={ingestLive} streamStatus={streamStatus} metrics={metrics}
        problems={problems} onProblemFix={handleProblemFix} onProblemDismiss={handleProblemDismiss}
        onChooseSource={(kind) => {
          setSourceKind(kind);
          closeSetup();
          setTimeout(() => document.getElementById(SECTION_IDS.source)
            ?.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
        }}
      />

      <ManageDestinationsDialog
        open={destDialogOpen}
        onClose={requestCloseDest}
        fullScreen={isMobile}
      >
        <OutputDestinationsPanel
          handleSubmit={handleSubmit} tabIndex={tabIndex} setTabIndex={setTabIndex}
          youtubeKeys={youtubeKeys} setYoutubeKeys={setYoutubeKeys}
          facebookKeys={facebookKeys} setFacebookKeys={setFacebookKeys}
          tiktokKey={tiktokKey} setTiktokKey={setTiktokKey}
          rtmpDestinations={rtmpDestinations} setRtmpDestinations={setRtmpDestinations}
          platformConnections={platformConnections} connectBusy={connectBusy}
          connectPlatform={connectPlatform} disconnectPlatform={disconnectPlatform}
          toggleConnection={toggleConnection} saveMetadata={saveMetadata}
          destLabels={destLabels} setDestLabels={setDestLabels}
          saved={savedKeys} unsaved={unsavedDest}
          onDiscard={discardDestChanges} onClose={requestCloseDest}
        />
      </ManageDestinationsDialog>

      <ConfirmDialog
        open={discardOpen}
        onClose={() => setDiscardOpen(false)}
        onConfirm={() => { discardDestChanges(); setDestDialogOpen(false); }}
        icon={<DeleteOutlineIcon />}
        title="Discard unsaved changes?"
        body="The keys and labels you changed haven't been saved. Discarding puts back what was there before."
        cancelLabel="Keep editing"
        confirmLabel="Discard changes"
      />

      {isMobile && (
        <Stack
          direction="row" alignItems="center" justifyContent="space-between"
          sx={{
            position: "sticky", top: 0, zIndex: 20, px: 2, py: 1.25,
            bgcolor: "rgba(16,15,39,0.92)", backdropFilter: "blur(16px)",
            borderBottom: `1px solid ${COLORS.border}`
          }}
        >
          <Wordmark height={24} />
          <Box sx={{ flex: 1 }} />
          {/* Whether you're broadcasting, visible from anywhere on the page. */}
          {(ingestLive || fileStreamLive) && (
            <Box component="span" role="status" sx={{
              display: "inline-flex", alignItems: "center", gap: 0.75, height: 24, px: 1.25, mr: 0.5,
              borderRadius: 99, fontSize: 11, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase",
              color: COLORS.green, bgcolor: "rgba(61,214,140,0.12)", border: "1px solid rgba(61,214,140,0.34)"
            }}>
              <Box component="span" sx={{
                width: 7, height: 7, borderRadius: "50%", bgcolor: "currentColor",
                animation: "pulse 1.4s ease-in-out infinite",
                "@media (prefers-reduced-motion: reduce)": { animation: "none" }
              }} />
              On air
            </Box>
          )}
          <IconButton onClick={() => setMobileOpen(true)} aria-label="Open menu" sx={{ color: COLORS.text }}>
            <MenuIcon />
          </IconButton>
        </Stack>
      )}

      <Box sx={{ display: "flex", minHeight: "100vh" }}>
        {isMobile ? (
          <Drawer
            anchor="left" open={mobileOpen} onClose={() => setMobileOpen(false)}
            PaperProps={{ sx: { width: SIDEBAR_WIDTH, bgcolor: COLORS.band, borderRight: `1px solid ${COLORS.border}` } }}
          >
            <Sidebar
              platforms={platforms} liveCount={liveCount}
              isMobile={isMobile} setSidebarOpen={setSidebarOpen}
              onNavigate={() => setMobileOpen(false)}
              account={accountBar} onOpenSetup={openSetup} storage={storageSummary}
            />
          </Drawer>
        ) : (
          <Drawer
            variant="permanent"
            sx={{
              width: sidebarOpen ? SIDEBAR_WIDTH : 0, flexShrink: 0, transition: "width 180ms ease",
              "& .MuiDrawer-paper": {
                width: sidebarOpen ? SIDEBAR_WIDTH : 0, boxSizing: "border-box",
                borderRight: `1px solid ${COLORS.border}`, overflow: "hidden",
                transition: "width 180ms ease"
              }
            }}
          >
            <Sidebar
              platforms={platforms} liveCount={liveCount}
              isMobile={isMobile} setSidebarOpen={setSidebarOpen}
              account={accountBar} onOpenSetup={openSetup} storage={storageSummary}
            />
          </Drawer>
        )}

        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          {/* The account bar (name + log out) used to take a full row here; it
              now sits at the bottom of the sidebar. Bottom padding reserves
              room for the fixed mobile tab bar. */}
          <Box sx={{
            px: { xs: 2, md: 4 },
            pt: { xs: 2, md: 4 },
            pb: isMobile ? `calc(${NAV_HEIGHT}px + 24px)` : 5
          }}>
            <Stack spacing={{ xs: 2, sm: 3 }}>

              <PageHeader
                fullName={fullName} destinationCount={platforms.length}
                liveCount={liveCount} bitrate={metrics?.bitrate ?? null}
                uptimeLabel={uptimeLabel} bitrateHistory={bitrateHistory}
                onAir={ingestLive || fileStreamLive}
                isMobile={isMobile}
              />

              <SecurityBanners showMessage={showMessage} onAccountChanged={fetchKeys} />

              <ProblemAlerts
                problems={problems}
                onFix={handleProblemFix}
                onDismiss={handleProblemDismiss}
              />

              {/* Not on phones: the stat tiles and the destination list below
                  say the same thing in a fraction of the height. */}
              <Box sx={{ display: { xs: "none", sm: "block" } }}>
                <SignalPath
                  sourceKind={sourceKind} ingestLive={ingestLive} fileStreamLive={fileStreamLive}
                  srtLive={srtStatus === "live"} srtCameraConnected={srtCameraConnected} metrics={metrics}
                  platforms={platforms} streamStatus={streamStatus} liveCount={liveCount}
                />
              </Box>

              {/* The header's chip row wraps into an unreadable pile at 390pt,
                  so phones get the same facts as fixed tiles instead. */}
              {isMobile && (
                <MobileStatBar
                  metrics={metrics} liveCount={liveCount}
                  destinationCount={platforms.length} uptimeLabel={uptimeLabel}
                  onAir={ingestLive || fileStreamLive}
                />
              )}

              {/* The Box matters: Stack resets its children's margins, which
                  cancelled the Grid's negative margin and pushed every card
                  right by one gap, past the page's right edge on a phone. */}
              <Box>
              <Grid container spacing={{ xs: 2, sm: 3 }}>

                {/* ════════════ LEFT: one Source card ════════════
                    Switcher, picture and the selected source's settings in one
                    card. They used to be three stacked cards with the switcher
                    sitting between the picture and its own settings.
                    The ids are the Sidebar's "Jump to" targets (SECTION_IDS in
                    Sidebar.jsx); keep them in sync. */}
                <Grid item xs={12} lg={7}>
                  <Box id={SECTION_IDS.preview} sx={{ scrollMarginTop: 80 }}>
                    <SourcePanel source={sourceKind} setSource={setSourceKind}>
                      {/* SRT goes camera -> server -> player and never passes
                          through the media server, so there is no picture to
                          show: its tab skips the preview instead of filling the
                          card with an empty black frame. The panel stays
                          MOUNTED (hidden) because it owns the <video> the FLV
                          player is attached to. */}
                      <Box sx={{ display: sourceKind === "srt" ? "none" : "block", mb: 3 }}>
                        <LivePreviewPanel
                          previewUrl={activePreviewUrl} startPreview={startPreview}
                          stopPreview={stopPreview} videoRef={videoRef}
                          sourceKind={sourceKind}
                          uptimeLabel={uptimeLabel} fps={metrics?.fps ?? null}
                          emptyStateSlot={
                            sourceKind === "file" ? (
                              <Mp4Dropzone
                                fileInputRef={fileInputRef}
                                uploadState={uploadState}
                                handleFileSelect={handleFileSelect}
                                abortUpload={abortUpload}
                                onNotice={showMessage}
                              />
                            ) : sourceKind === "browser" ? (
                              // Just an empty box: BrowserEncodePanel portals the
                              // live <video> into it, keeping the stream in its
                              // own tree. See its `previewHost` prop.
                              <Box
                                ref={setCameraPreviewHost}
                                sx={{
                                  width: "100%", height: "100%", display: "flex",
                                  alignItems: "center", justifyContent: "center"
                                }}
                              />
                            ) : null
                          }
                        />
                      </Box>

                      {/* All four stay MOUNTED and are hidden with display:none
                          rather than conditionally rendered. Unmounting
                          BrowserEncodePanel would tear down a live getUserMedia
                          stream, its MediaRecorder and the /browser-encode
                          WebSocket, so merely looking at your RTMP credentials
                          would end a browser broadcast. Mp4SchedulePanel
                          likewise holds state across a multi-minute chunked
                          upload. See SourcePanel.jsx. */}
                      <Box
                        id={SECTION_IDS.source}
                        sx={{
                          scrollMarginTop: 80,
                          pt: sourceKind === "srt" ? 0 : 3,
                          borderTop: sourceKind === "srt" ? "none" : `1px solid ${COLORS.border}`
                        }}
                      >
                        <Box sx={{ display: sourceKind === "rtmp" ? "block" : "none" }}>
                          <RtmpIngestPanel
                            rtmpUrl={rtmpUrl} rtmpToken={rtmpToken} displayUserId={displayUserId}
                            generateStreamToken={generateStreamToken} keyExpiresAt={rtmpKeyExpiresAt}
                            keyMasked={rtmpKeyMasked}
                            copiedField={copiedField} copyToClipboard={copyToClipboard}
                          />
                        </Box>

                        <Box sx={{ display: sourceKind === "browser" ? "block" : "none" }}>
                          <BrowserEncodePanel showMessage={showMessage} previewHost={cameraPreviewHost} />
                        </Box>

                        <Box sx={{ display: sourceKind === "srt" ? "block" : "none" }}>
                          <FieldReportersPanel
                            axiosConfig={axiosConfig} showMessage={showMessage}
                            copiedField={copiedField} copyToClipboard={copyToClipboard}
                          />
                        </Box>

                        <Box sx={{ display: sourceKind === "file" ? "block" : "none" }}>
                          <Mp4SchedulePanel
                            // The upload props went with the dropzone to the preview
                            // frame; uploadState stays because Schedule is disabled
                            // while an upload is still running.
                            uploadState={uploadState}
                            storageSummary={storageSummary} uploadedVideos={uploadedVideos}
                            selectedVideoId={selectedVideoId} setSelectedVideoId={setSelectedVideoId}
                            deleteVideo={deleteVideo}
                            handleSchedule={handleSchedule} scheduledAt={scheduledAt}
                            setScheduledAt={setScheduledAt} schedulerBusy={schedulerBusy}
                            axiosConfig={axiosConfig}
                            onDeleteJob={(job, onError) => {
                              setDeleteTarget({ ...job, _onError: onError });
                              setDeleteDialogOpen(true);
                            }}
                          />
                        </Box>
                      </Box>
                    </SourcePanel>
                  </Box>
                </Grid>

                {/* ════════════ RIGHT COLUMN ════════════ */}
                <Grid item xs={12} lg={5}>
                  <Stack spacing={3} sx={{ position: { lg: "sticky" }, top: { lg: 24 } }}>
                    {/* First in the column on purpose: "where is my stream
                        going, and is it arriving?" is the question this page
                        exists to answer. */}
                    <Box id={SECTION_IDS.destinations} sx={{ scrollMarginTop: 80 }}>
                      <DestinationsPanel
                        platforms={platforms} streamStatus={streamStatus}
                        isLive={ingestLive} retryingIds={retryingIds}
                        restartPlatformApi={restartPlatformApi} stopPlatformApi={stopPlatformApi}
                        fileStreamLive={fileStreamLive} platformStats={platformStats}
                        onManage={() => setDestDialogOpen(true)}
                        problems={problems}
                      />
                    </Box>

                    <Box id={SECTION_IDS.diagnostics} sx={{ scrollMarginTop: 80 }}>
                      <SignalDiagnosticsPanel metrics={metrics} bitrateHistory={bitrateHistory} />
                    </Box>
                    {fileJobs.some((j) => j.status === "scheduled") && (
                    <Box>
                      <UpNextPanel
                        jobs={fileJobs}
                        onCancel={cancelFileJob}
                        onOpenScheduler={() => {
                          setSourceKind("file");
                          document.getElementById(SECTION_IDS.source)
                            ?.scrollIntoView({ behavior: "smooth", block: "start" });
                        }}
                      />
                    </Box>
                    )}
                  </Stack>
                </Grid>
              </Grid>
              </Box>
            </Stack>
          </Box>
        </Box>
      </Box>
    </Box>
    </ThemeProvider>
  );
}
